import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, TextInput, SectionTitle, ConfigRequired } from "@/lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Mail, ShieldCheck, Server, Users2, Plus, LineChart, Trash2, Wrench, BellOff, Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ROLES = ["owner", "admin", "finance", "marketing", "content", "operations", "readonly"];

function OrgTab() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: org, isLoading } = useQuery({ queryKey: ["org"], queryFn: () => api.get("/settings/org") });
  const [form, setForm] = useState(null);
  const save = useMutation({
    mutationFn: (body) => api.patch("/settings/org", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org"] }); toast.success("Organisation settings saved"); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <PageLoader />;
  const v = form ?? org;
  return (
    <Panel className="p-5 max-w-xl space-y-3">
      <TextInput label="Organisation name" value={v.org_name || ""} onChange={(e) => setForm({ ...v, org_name: e.target.value })} data-testid="org-name" />
      <TextInput label="Primary domain" value={v.primary_domain || ""} onChange={(e) => setForm({ ...v, primary_domain: e.target.value })} />
      <TextInput label="Default AI model" value={v.default_ai_model || ""} onChange={(e) => setForm({ ...v, default_ai_model: e.target.value })} />
      <TextInput label="Default environment" value={v.default_environment || ""} onChange={(e) => setForm({ ...v, default_environment: e.target.value })} />
      <Btn onClick={() => save.mutate(v)} disabled={save.isPending} data-testid="save-org">Save Settings</Btn>
    </Panel>
  );
}

function InviteDialog() {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("operations");
  const invite = useMutation({
    mutationFn: () => api.post("/users/invite", { email, role }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); setEmail("");
      toast.success(`Invited ${r.email} as ${r.role} (${r.invite_status})`); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Btn size="sm" data-testid="invite-user-btn"><Plus className="h-4 w-4" /> Invite Teammate</Btn></DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle className="font-head">Invite Teammate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <TextInput label="Email" placeholder="teammate@gopigadhvi.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />
          <label className="block">
            <span className="text-xs text-muted-foreground mb-1.5 block">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role"
              className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm">
              {ROLES.filter((r) => r !== "owner").map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <p className="text-xs text-muted-foreground">They receive a Clerk invitation email. Their role is applied automatically on first sign-in.</p>
        </div>
        <DialogFooter><Btn onClick={() => invite.mutate()} disabled={!email || invite.isPending} data-testid="invite-submit">Send Invite</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users") });
  const { data: rolesData } = useQuery({ queryKey: ["roles"], queryFn: () => api.get("/roles") });
  const update = useMutation({
    mutationFn: ({ id, role }) => api.patch(`/users/${id}`, { role }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Role updated"); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <PageLoader />;
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><InviteDialog /></div>
      <Panel className="divide-y divide-border">
        {users.map((u) => (
          <div key={u.clerk_user_id} className="flex items-center gap-4 p-3" data-testid={`user-${u.clerk_user_id}`}>
            <div className="h-8 w-8 rounded-full bg-accent flex items-center justify-center text-xs font-mono">{(u.name || u.email || "?")[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{u.name || u.email}</div>
              <div className="text-[11px] font-mono text-muted-foreground truncate">{u.email}</div>
            </div>
            {u.status === "INVITED" && <StatusBadge tone="warn">Invited</StatusBadge>}
            {u.role === "owner" ? (
              <StatusBadge tone="gold">Owner</StatusBadge>
            ) : (
              <select value={u.role} onChange={(e) => update.mutate({ id: u.clerk_user_id, role: e.target.value })}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs" data-testid={`role-select-${u.clerk_user_id}`}>
                {(rolesData?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
}

function MetricsTab() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const [kind, setKind] = useState("finance");
  const { data, isLoading } = useQuery({ queryKey: ["metrics", brand, kind], queryFn: () => api.get(`/metrics?brand_id=${brand}&kind=${kind}`) });
  const [items, setItems] = useState(null);
  const rows = items ?? (data?.items || []);
  const setRow = (i, field, val) => { const next = [...rows]; next[i] = { ...next[i], [field]: val }; setItems(next); };
  const addRow = () => setItems([...rows, { label: "", value: "" }]);
  const removeRow = (i) => setItems(rows.filter((_, idx) => idx !== i));
  const persist = useMutation({
    mutationFn: () => api.put("/metrics", { brand_id: brand, kind, items: rows.filter((r) => r.label) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["metrics"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setItems(null); toast.success("Metrics saved — now live on Today"); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <PageLoader />;
  return (
    <Panel className="p-5 max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        {["finance", "analytics"].map((k) => (
          <button key={k} onClick={() => { setKind(k); setItems(null); }} data-testid={`metrics-kind-${k}`}
            className={`text-xs uppercase tracking-wider border rounded-full px-3 py-1 transition-colors ${kind === k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
            {k}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto font-mono">{brand}</span>
      </div>
      <SectionTitle className="mb-2">{kind} summary rows (shown on Today)</SectionTitle>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-xs text-muted-foreground">No rows yet. Add a label + value below.</div>}
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2">
            <TextInput placeholder="Label (e.g. MRR)" value={r.label || ""} onChange={(e) => setRow(i, "label", e.target.value)} className="flex-1" data-testid={`metric-label-${i}`} />
            <TextInput placeholder="Value (e.g. ₹4.2M)" value={r.value || ""} onChange={(e) => setRow(i, "value", e.target.value)} className="flex-1" data-testid={`metric-value-${i}`} />
            <Btn size="icon" variant="ghost" onClick={() => removeRow(i)}><Trash2 className="h-4 w-4" /></Btn>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Btn variant="outline" size="sm" onClick={addRow} data-testid="metric-add-row"><Plus className="h-4 w-4" /> Add Row</Btn>
        <Btn size="sm" onClick={() => persist.mutate()} disabled={persist.isPending} data-testid="metric-save">Save Metrics</Btn>
      </div>
    </Panel>
  );
}

function EmailTab() {
  const api = useApi();
  const [to, setTo] = useState("");
  const send = useMutation({
    mutationFn: () => api.post("/email/test", { to }),
    onSuccess: () => toast.success("Test email sent via Resend"),
    onError: (e) => toast.error(e.message),
  });
  const { data: logs = [] } = useQuery({ queryKey: ["email-logs"], queryFn: () => api.get("/email/logs") });
  return (
    <div className="space-y-4 max-w-2xl">
      <Panel className="p-5">
        <SectionTitle className="mb-3">Send Test Email (real Resend request)</SectionTitle>
        <div className="flex gap-2">
          <TextInput placeholder="recipient@example.com" value={to} onChange={(e) => setTo(e.target.value)} className="flex-1" data-testid="email-test-to" />
          <Btn onClick={() => send.mutate()} disabled={!to || send.isPending} data-testid="send-test-email"><Mail className="h-4 w-4" /> Send</Btn>
        </div>
      </Panel>
      <Panel className="divide-y divide-border">
        <div className="p-3"><SectionTitle>Email Log</SectionTitle></div>
        {logs.length === 0 ? <div className="p-3 text-xs text-muted-foreground">No emails sent yet.</div> :
          logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between p-3 text-xs font-mono">
              <span className="truncate">{l.recipient} · {l.template}</span>
              <StatusBadge tone={l.status === "SENT" ? "ok" : "err"}>{l.status}</StatusBadge>
            </div>
          ))}
      </Panel>
    </div>
  );
}

function SecurityTab() {
  const api = useApi();
  const { data: cfg } = useQuery({ queryKey: ["auth-config"], queryFn: () => api.get("/auth/config") });
  const rows = [
    ["Clerk Authentication", cfg?.clerk_configured, ShieldCheck],
    ["MeshAPI Orchestration", cfg?.mesh_configured, Server],
    ["Resend Email", cfg?.email_configured, Mail],
  ];
  return (
    <Panel className="divide-y divide-border max-w-xl">
      {rows.map(([label, ok, Icon]) => (
        <div key={label} className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" /><span className="text-sm">{label}</span></div>
          {ok ? <StatusBadge tone="ok" dot>Configured</StatusBadge> : <ConfigRequired />}
        </div>
      ))}
      <div className="p-4 text-xs text-muted-foreground leading-relaxed">
        All secrets are stored server-side. Sessions are verified via Clerk on every request.
        Role-based permissions and audit logging are enforced across the API.
      </div>
    </Panel>
  );
}

function MaintenanceTab() {
  const api = useApi();
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: (action) => api.post("/maintenance/purge", { action }),
    onSuccess: (r) => {
      const msg = r.marked_read !== undefined ? `${r.marked_read} marked read`
        : r.notifications_deleted !== undefined ? `${r.notifications_deleted} notifications cleared`
        : r.agent_runs_deleted !== undefined ? `${r.agent_runs_deleted} test AI runs purged` : "Done";
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["notif-count"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["exec-dashboard"] });
    },
    onError: (e) => toast.error(e.message),
  });
  const actions = [
    { action: "notifications_mark_read", icon: BellOff, title: "Mark all notifications read", desc: "Clears the unread badge without deleting anything.", danger: false, cta: "Mark all read" },
    { action: "notifications_clear", icon: Trash2, title: "Clear all notifications", desc: "Permanently deletes every notification. Use on a fresh install to remove demo activity.", danger: true, cta: "Clear all" },
    { action: "ai_runs_purge_test", icon: Sparkles, title: "Purge test AI runs", desc: "Removes concierge/test agent runs so the AI Recommendations feed shows only real analyses.", danger: true, cta: "Purge test runs" },
  ];
  return (
    <div className="max-w-2xl space-y-3" data-testid="maintenance-tab">
      <Panel className="p-4 flex items-start gap-3">
        <Wrench className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">Owner/admin tools to clean up demo &amp; test data so your dashboard reflects only real activity. These run on whichever environment you're in (preview or production).</p>
      </Panel>
      {actions.map((a) => (
        <Panel key={a.action} className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <a.icon className={`h-4 w-4 mt-0.5 shrink-0 ${a.danger ? "text-err" : "text-muted-foreground"}`} />
            <div className="min-w-0">
              <div className="text-sm font-medium">{a.title}</div>
              <div className="text-xs text-muted-foreground">{a.desc}</div>
            </div>
          </div>
          {a.danger ? (
            <AlertDialog>
              <AlertDialogTrigger asChild><Btn variant="outline" size="sm" className="shrink-0" data-testid={`maint-${a.action}`}>{a.cta}</Btn></AlertDialogTrigger>
              <AlertDialogContent className="bg-surface border-border">
                <AlertDialogHeader><AlertDialogTitle>{a.title}?</AlertDialogTitle><AlertDialogDescription>{a.desc} This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => run.mutate(a.action)} className="bg-err text-white hover:bg-err/90" data-testid={`maint-confirm-${a.action}`}>{a.cta}</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Btn variant="outline" size="sm" className="shrink-0" onClick={() => run.mutate(a.action)} disabled={run.isPending} data-testid={`maint-${a.action}`}>{a.cta}</Btn>
          )}
        </Panel>
      ))}
    </div>
  );
}

export default function Settings() {
  return (
    <div>
      <PageHeader kicker="No-Code Control" title="Settings" />
      <Tabs defaultValue="org">
        <TabsList className="bg-surface border border-border">
          <TabsTrigger value="org" data-testid="tab-org">Organisation</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users2 className="h-3.5 w-3.5 mr-1" /> Users &amp; Roles</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="tab-metrics"><LineChart className="h-3.5 w-3.5 mr-1" /> Metrics</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench className="h-3.5 w-3.5 mr-1" /> Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="org" className="mt-4"><OrgTab /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="metrics" className="mt-4"><MetricsTab /></TabsContent>
        <TabsContent value="email" className="mt-4"><EmailTab /></TabsContent>
        <TabsContent value="security" className="mt-4"><SecurityTab /></TabsContent>
        <TabsContent value="maintenance" className="mt-4"><MaintenanceTab /></TabsContent>
      </Tabs>
    </div>
  );
}
